import { clientConfig, getSdk, notify, text } from "@quickpoint.me/shared";
import type { SDK_GenerateLayoutMutationVariables, SDK_Stack } from "@quickpoint.me/shared";
import { getAuth } from "firebase/auth";
import { GraphQLClient } from "graphql-request";
import { updateFiles } from "../lib/messageBus";

/** Extended variables type that includes createMode for the streaming endpoint */
type StreamVariables = SDK_GenerateLayoutMutationVariables & {
	createMode?: boolean;
};
import { useCallback, useRef } from "react";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";
import { postAiData } from "../lib/ai/postAiData";
import {
	HUMAN_HELP_MARKER,
	escalateToHuman,
	forwardToHuman,
	getActiveConversationId,
} from "../lib/support";
import { ShowTknsPanelAtom } from "../state/tknsAtoms";
import {
	extractPlaceholderContext,
	findGeneratePlaceholders,
	searchForPlaceholders,
	applyImageSelections,
	processGeneratedCode,
} from "../lib/ai/processGeneratedCode";
import {
	AiChatHistoryAtom,
	AiChatLevelAtom,
	AiImageQueueAtom,
	AiImageRetryAtom,
	AiImagePickerAtom,
	AiImagePickerDiscardAtom,
	AiImagePickerGenerateAtom,
	AiImagePickerResolveAtom,
	AiIsStreamingAtom,
	type ChatMessage,
	type GeneratedImage,
	type ImagePickerSelection,
} from "../state/aiChatAtoms";
import { HostId } from "../state/atoms";
import { OwnerName } from "../state/authSelectors";
import { StackCfKeyAtom, StackAtomFamily } from "../state/stackAtoms";
import { StackSelectorFamily } from "../state/stackSelectors";

interface StreamEvent {
	type: "jadle" | "explanation" | "done" | "error";
	chunk?: string;
	message?: string;
	jadle?: string; // Included in 'done' event for createMode
	explanation?: string; // Included in 'done' event for createMode
}

export function useAiStream() {
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	const setChatHistory = useSetRecoilState(AiChatHistoryAtom(stackCfKey));
	const setIsStreaming = useSetRecoilState(AiIsStreamingAtom);
	const setShowTknsPanel = useSetRecoilState(ShowTknsPanelAtom);
	const setAiChatLevel = useSetRecoilState(AiChatLevelAtom);
	const setImageQueue = useSetRecoilState(AiImageQueueAtom);
	const setImageRetry = useSetRecoilState(AiImageRetryAtom);
	const setImagePicker = useSetRecoilState(AiImagePickerAtom);
	const setImagePickerResolve = useSetRecoilState(AiImagePickerResolveAtom);
	const setImagePickerGenerate = useSetRecoilState(AiImagePickerGenerateAtom);
	const setImagePickerDiscard = useSetRecoilState(AiImagePickerDiscardAtom);
	const abortRef = useRef<AbortController | null>(null);

	const setStackCode = useRecoilCallback(
		({ set, snapshot }) =>
			(newCode: string) => {
				if (!stackCfKey) return;
				const loadable = snapshot.getLoadable(StackAtomFamily(stackCfKey));
				if (loadable.state !== "hasValue") return;
				set(StackSelectorFamily(stackCfKey), (oldStack: SDK_Stack) => ({
					...oldStack,
					code: newCode,
				}));
			},
		[stackCfKey],
	);

	// Escalation to a human: read the transcript and open a support
	// conversation the first time the AI emits the [[HUMAN_HELP]] marker
	const escalateWithTranscript = useRecoilCallback(
		({ snapshot }) =>
			() => {
				if (!stackCfKey || getActiveConversationId()) return;
				const loadable = snapshot.getLoadable(AiChatHistoryAtom(stackCfKey));
				const history =
					loadable.state === "hasValue" ? loadable.contents : [];
				void escalateToHuman(stackCfKey, history);
			},
		[stackCfKey],
	);

	// For image generation processing
	const ownerName = useRecoilValue(OwnerName);
	const hostId = useRecoilValue(HostId);
	const stack = useRecoilValue(StackAtomFamily(stackCfKey));
	const stackId = stack?.stackId || "";

	const cancelStream = useCallback(() => {
		abortRef.current?.abort();
		abortRef.current = null;
		setIsStreaming(false);
	}, [setIsStreaming]);

	/**
	 * Create-mode image flow: search public domain → show picker → import/generate.
	 * Returns the final processed code.
	 */
	const runImagePickerFlow = useCallback(
		async (jadleCode: string): Promise<string> => {
			const { code, placeholders } = findGeneratePlaceholders(jadleCode);
			if (placeholders.length === 0) return code;

			const contexts = placeholders.map((p) =>
				extractPlaceholderContext(code, p),
			);

			// Show searching state in picker
			setImagePicker(
				placeholders.map((p, i) => ({
					prompt: p.prompt,
					results: [],
					status: "searching" as const,
					context: contexts[i],
				})),
			);

			// Search public domain sources
			const slots = await searchForPlaceholders(placeholders);
			setImagePicker(slots.map((s, i) => ({ ...s, context: contexts[i] })));

			// In-dialog generation for the refinement loop: each call persists
			// an image and hands the picker a new strip candidate
			setImagePickerGenerate(() => async (prompt: string) => {
				try {
					const auth = getAuth();
					const jwt = await auth.currentUser?.getIdToken();
					if (!jwt || !ownerName) return null;
					const client = new GraphQLClient(clientConfig.gqlGenerateAiImage());
					const sdk = getSdk(client);
					const res = await sdk.GenerateAiImage({
						prompt,
						jwt,
						ownerName,
						stackId,
					});
					if (res.generateAiImage?.__typename === "QpFile" && res.generateAiImage.url) {
						updateFiles(hostId);
						return {
							generatedName: res.generateAiImage.userDefinedName,
							fileId: res.generateAiImage.id,
							url: res.generateAiImage.url,
							prompt,
						};
					}
					return null;
				} catch (e) {
					console.error("In-picker generation failed", e);
					return null;
				}
			});

			// delete generated-but-unused images if the user chooses to discard
			setImagePickerDiscard(() => async (images: GeneratedImage[]) => {
				try {
					const auth = getAuth();
					const jwt = await auth.currentUser?.getIdToken();
					if (!jwt) return;
					const client = new GraphQLClient(clientConfig.gqlWriteFile());
					const sdk = getSdk(client);
					await Promise.allSettled(
						images.map((img) => sdk.DeleteFile({ fileId: img.fileId, jwt })),
					);
					updateFiles(hostId);
				} catch (e) {
					console.error("Discarding unused generated images failed", e);
				}
			});

			// Wait for user selections
			const selections = await new Promise<ImagePickerSelection[]>(
				(resolve) => {
					setImagePickerResolve(() => resolve);
				},
			);

			// Clear picker
			setImagePicker(null);
			setImagePickerResolve(null);
			setImagePickerGenerate(null);
			setImagePickerDiscard(null);

			// Import/generate selected images
			return applyImageSelections(code, placeholders, selections, {
				ownerName: ownerName || "",
				stackId,
				hostId,
				onQueueInit: (queue) => setImageQueue(queue),
				onImageProgress: (updater) => setImageQueue(updater),
			});
		},
		[ownerName, stackId, hostId, setImagePicker, setImagePickerResolve, setImagePickerGenerate, setImagePickerDiscard, setImageQueue],
	);

	const sendMessage = useCallback(
		async (
			variables: StreamVariables,
			userMessage: string,
		) => {
			const messageId = crypto.randomUUID();
			const assistantId = crypto.randomUUID();

			// Add user message to history
			const userMsg: ChatMessage = {
				id: messageId,
				role: "user",
				content: userMessage,
				timestamp: Date.now(),
			};

			setChatHistory((prev) => [...prev, userMsg]);
			if (getActiveConversationId()) forwardToHuman(userMessage);
			setIsStreaming(true);

			// Add placeholder assistant message
			const assistantMsg: ChatMessage = {
				id: assistantId,
				role: "assistant",
				content: "",
				timestamp: Date.now(),
				isStreaming: true,
			};
			setChatHistory((prev) => [...prev, assistantMsg]);

			// Try SSE streaming first
			const abortController = new AbortController();
			abortRef.current = abortController;

			// the AI worker now requires a valid session
			const jwt = await getAuth().currentUser?.getIdToken();

			try {
				const streamUrl = `${clientConfig.layoutAi()}/stream`;
				const response = await fetch(streamUrl, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ ...variables, jwt }),
					signal: abortController.signal,
				});

				// rate-limited: tell the user plainly and stop — falling through
				// to the non-streaming fallback would just 429 again
				if (response.status === 429) {
					notify(text.chat.rateLimited);
					setChatHistory((prev) =>
						prev.filter((m) => m.id !== assistantId),
					);
					setIsStreaming(false);
					return;
				}

				// out of tkns or daily cap hit: open the TKNS panel —
				// this is the conversion moment, don't bury it in a toast
				if (response.status === 402) {
					const body = await response.json().catch(() => ({}) as { error?: string });
					notify(body.error === "daily cap reached" ? text.tkns.dailyCap : text.tkns.insufficient);
					setShowTknsPanel(true);
					setChatHistory((prev) =>
						prev.filter((m) => m.id !== assistantId),
					);
					setIsStreaming(false);
					return;
				}

				if (!response.ok || !response.body) {
					throw new Error("SSE not available");
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let jadleBuffer = "";
				let explanationBuffer = "";
				let sseBuffer = "";

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;

					sseBuffer += decoder.decode(value, { stream: true });
					const lines = sseBuffer.split("\n");
					sseBuffer = lines.pop() || "";

					for (const line of lines) {
						if (!line.startsWith("data: ")) continue;
						const raw = line.slice(6).trim();
						if (!raw) continue;

						let event: StreamEvent;
						try {
							event = JSON.parse(raw);
						} catch {
							continue;
						}

						if (event.type === "jadle" && event.chunk) {
							jadleBuffer += event.chunk;
						} else if (event.type === "explanation" && event.chunk) {
							explanationBuffer += event.chunk;
							const visible = explanationBuffer
								.replace(HUMAN_HELP_MARKER, "")
								.trimEnd();
							setChatHistory((prev) =>
								prev.map((msg) =>
									msg.id === assistantId
										? {
												...msg,
												content: visible,
												jadle: jadleBuffer,
											}
										: msg,
								),
							);
						} else if (event.type === "done") {
							// Use jadle from event if present (createMode), otherwise use buffer
							const finalJadle = event.jadle || jadleBuffer;
							const isCreateMode = variables.createMode && finalJadle;

							if (isCreateMode) {
								const explanation = event.explanation || explanationBuffer || "Presentation created!";

								const finalize = (code: string) => {
									setImageQueue([]);
									setImageRetry(null);
									setAiChatLevel(3);
									setStackCode(code);
									setChatHistory((prev) =>
										prev.map((msg) =>
											msg.id === assistantId
												? { ...msg, content: explanation, jadle: code, isStreaming: false }
												: msg,
										),
									);
								};

								const runWithPicker = async (code: string) => {
									const processedCode = await runImagePickerFlow(code);

									// Check if any GENERATE: placeholders remain (failed images)
									const hasRemaining = /(image|background):\s*(image\s*)?"?GENERATE:/i.test(processedCode);

									if (hasRemaining) {
										setImageRetry({
											retry: () => runWithPicker(processedCode),
											continueAnyway: () => finalize(processedCode),
										});
									} else {
										finalize(processedCode);
									}
								};

								await runWithPicker(finalJadle);
							} else if (finalJadle) {
								// Non-create mode with jadle - just update code
								processGeneratedCode(finalJadle, {
									ownerName: ownerName || "",
									stackId,
									hostId,
								}).then((processedCode) => {
									setStackCode(processedCode);
									setChatHistory((prev) =>
										prev.map((msg) =>
											msg.id === assistantId
												? {
														...msg,
														content:
															explanationBuffer || "Presentation updated.",
														jadle: processedCode,
														isStreaming: false,
													}
												: msg,
										),
									);
								});
							} else {
								// Chat mode - no jadle, just explanation
								const wantsHuman =
									explanationBuffer.includes(HUMAN_HELP_MARKER);
								const clean =
									explanationBuffer
										.replace(HUMAN_HELP_MARKER, "")
										.trim() || "Response complete.";
								setChatHistory((prev) =>
									prev.map((msg) =>
										msg.id === assistantId
											? {
													...msg,
													content: clean,
													isStreaming: false,
												}
											: msg,
									),
								);
								if (wantsHuman) escalateWithTranscript();
							}
						} else if (event.type === "error") {
							setChatHistory((prev) =>
								prev.map((msg) =>
									msg.id === assistantId
										? {
												...msg,
												content:
													event.message || "An error occurred.",
												isStreaming: false,
											}
										: msg,
								),
							);
						}
					}
				}
			} catch (e) {
				if (abortController.signal.aborted) {
					// User cancelled — remove streaming message
					setChatHistory((prev) =>
						prev.filter((msg) => msg.id !== assistantId),
					);
					setIsStreaming(false);
					return;
				}

				// Fallback to GQL mutation (non-streaming)
				// GQL only supports generateLayout (always produces code),
				// so only use it when the user explicitly hit Create.
				if (!variables.createMode) {
					setChatHistory((prev) =>
						prev.map((msg) =>
							msg.id === assistantId
								? {
										...msg,
										content: "Chat is temporarily unavailable. Please try again.",
										isStreaming: false,
									}
								: msg,
						),
					);
				} else {
					try {
						const result = await postAiData(variables, jwt);

						if (result?.__typename === "AiChatResponse") {
							const gqlExplanation = result.explanation || "Presentation created!";

							const gqlFinalize = (code: string) => {
								setImageQueue([]);
								setImageRetry(null);
								setAiChatLevel(3);
								setStackCode(code);
								setChatHistory((prev) =>
									prev.map((msg) =>
										msg.id === assistantId
											? { ...msg, content: gqlExplanation, jadle: code, isStreaming: false }
											: msg,
									),
								);
							};

							const gqlRunWithPicker = async (code: string) => {
								const processedCode = await runImagePickerFlow(code);

								const hasRemaining = /(image|background):\s*(image\s*)?"?GENERATE:/i.test(processedCode);

								if (hasRemaining) {
									setImageRetry({
										retry: () => gqlRunWithPicker(processedCode),
										continueAnyway: () => gqlFinalize(processedCode),
									});
								} else {
									gqlFinalize(processedCode);
								}
							};

							await gqlRunWithPicker(result.jadle);
						} else if (result?.__typename === "StackErrorMessage") {
							setChatHistory((prev) =>
								prev.map((msg) =>
									msg.id === assistantId
										? {
												...msg,
												content: result.message,
												isStreaming: false,
											}
										: msg,
								),
							);
						}
					} catch (fallbackError) {
						console.error("AI fallback failed", fallbackError);
						setChatHistory((prev) =>
							prev.map((msg) =>
								msg.id === assistantId
									? {
											...msg,
											content: "Failed to generate. Please try again.",
											isStreaming: false,
										}
									: msg,
							),
						);
					}
				}
			} finally {
				setIsStreaming(false);
				abortRef.current = null;
			}
		},
		[stackCfKey, setChatHistory, setIsStreaming, setAiChatLevel, setImageQueue, setImageRetry, setStackCode, ownerName, stackId, hostId, runImagePickerFlow, escalateWithTranscript],
	);

	return { sendMessage, cancelStream };
}
